import { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import PickerFieldTable from '../../../../components/PickerFieldTable';
import { useNotifier } from '../../../../components/Notifier';
import { createTireTransaction } from '../../../../stores/tireStore';
import { useTireBatchListInventory } from '../../../../stores/tireBatchStore';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import { useLanguage } from '../../../../utils/i18n';

// We will build args dynamically inside the component instead
const formatTireBatchOption = (b) => ({
  value: b.tire_item || b.tire_item_id || b.id,
  label: `${b.serial_number || b.batch_number} (${b.join?.tire_item_tire_brand_name || b.tire_brand_name || b.join?.tire_brand_name || '-'} ${b.join?.tire_item_tire_spec_name || b.tire_item_tire_spec_name || b.join?.tire_item_tire_spec_name || '-'} ${b.join?.tire_item_tire_size_code || b.tire_item_tire_size_code || b.join?.tire_item_tire_size_code || '-'})`,
  original: b
});

export default function InstallForm({
  vehicleDetails,
  tireEquipVehicleId,
  selectedPosition,
  rawAxleData,
  hmUnit,
  kmUnit,
  date,
  onSuccess,
  onCancel,
  positionMap,
  queue,
  setQueue,
  limitInfo
}) {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [selectedTireId, setSelectedTireId] = useState(null);
  const [selectedTireData, setSelectedTireData] = useState(null);

  const [currentCondition, setcurrentCondition] = useState('Good');
  const [mechanicName, setMechanicName] = useState('');
  const [reason, setReason] = useState('');
  const [bulkAirPressure, setBulkAirPressure] = useState('');
  const [bulkTreadDepth, setBulkTreadDepth] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isAbnormalKmHm = useMemo(() => {
    if (kmUnit && hmUnit && Number(hmUnit) > 0) {
      const ratio = Number(kmUnit) / Number(hmUnit);
      if (ratio > 100 || ratio < 1) return true;
    }
    return false;
  }, [kmUnit, hmUnit]);

  const handleBulkAirPressureChange = (e) => {
    const val = e.target.value;
    setBulkAirPressure(val);
    setQueue(queue.map((q) => (q.hasExistingAirPressure ? q : { ...q, airPressure: val })));
  };

  const handleBulkTreadDepthChange = (e) => {
    const rawVal = e.target.value;
    setBulkTreadDepth(rawVal);
    setQueue(
      queue.map((q) => {
        if (q.hasExistingTreadDepth) return q;
        let val = parseFloat(rawVal);
        if (!isNaN(val) && q.originalTreadDepth && val > q.originalTreadDepth) {
          val = q.originalTreadDepth;
        }
        return { ...q, treadDepth: isNaN(val) ? rawVal : val };
      })
    );
  };

  const buildTireBatchArgs = (state) => {
    const vTypeId = vehicleDetails?.vehicle_type_id || vehicleDetails?.vehicle_type;
    const extraQuery = vTypeId ? `&is_need_action=false&vehicle_type_id=${vTypeId}` : '&is_need_action=false';
    return [state.page, state.pageSize, [], state.searchKey, state.searchValue, [], extraQuery];
  };

  // Fetch tire batch options using the proven useTireBatchListInventory
  const tireBatchPickerParams = usePickerPagination({
    fetchHook: useTireBatchListInventory,
    buildArgs: buildTireBatchArgs,
    formatOption: formatTireBatchOption,
    searchKey: 'serial_number'
  });

  const getValidValueWithSource = (v1, v2, v3) => {
    if (v1 != null && v1 !== 0 && String(v1) !== '0') return { value: v1, source: 'Last Inspection' };
    if (v2 != null && v2 !== 0 && String(v2) !== '0') return { value: v2, source: 'Base' };
    if (v3 != null && v3 !== 0 && String(v3) !== '0') return { value: v3, source: 'Standard' };
    return { value: null, source: null };
  };

  const tireColumns = [
    {
      field: 'serial_number',
      headerName: t('tireCardHistory.serialNumber', 'Serial Number'),
      renderCell: (r) => {
        const tireId = r.original?.tire_item || r.original?.tire_item_id || r.original?.id;
        const isQueued = queue.some((q) => q.tireId === tireId);
        const sn = r.original?.serial_number || '-';
        return isQueued ? `${sn} (${t('tireCycle.alreadyInQueue', 'Already in Queue')})` : sn;
      }
    },
    { field: 'brand', headerName: t('general.brand', 'Brand'), renderCell: (r) => r.original?.join?.tire_item_tire_brand_name || '-' },
    { field: 'pattern', headerName: t('general.pattern', 'Pattern'), renderCell: (r) => r.original?.join?.m_tire_spec_pattern || '-' },
    {
      field: 'size',
      headerName: t('general.size', 'Size'),
      renderCell: (r) => r.original?.join?.tire_item_tire_size_code_internal || r.original?.join?.tire_item_tire_size_code || '-'
    },
    {
      field: 'status',
      headerName: t('general.status', 'Status'),
      renderCell: (r) => (r.original?.status === 'REMOVE' ? t('tireCycle.inStock', 'In Stock') : r.original?.status || '-')
    },
    {
      field: 'air_pressure',
      headerName: t('general.airPressure', 'Air Pressure'),
      renderCell: (r) => {
        const result = getValidValueWithSource(
          r.original?.last_inspection?.air_pressure,
          r.original?.air_pressure,
          r.original?.join?.m_tire_spec_pressure_std
        );
        if (!result.value) return '-';
        return (
          <Box display="flex" flexDirection="column">
            <Typography variant="body2">{result.value}</Typography>
            <Typography variant="caption" color="text.secondary">
              {result.source}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'tread_depth',
      headerName: t('general.treadDepth', 'Tread Depth'),
      renderCell: (r) => {
        const result = getValidValueWithSource(
          r.original?.last_inspection?.tread_depth,
          r.original?.tread_depth,
          r.original?.join?.m_tire_spec_original_tread_depth
        );
        if (!result.value) return '-';
        return (
          <Box display="flex" flexDirection="column">
            <Typography variant="body2">{result.value}</Typography>
            <Typography variant="caption" color="text.secondary">
              {result.source}
            </Typography>
          </Box>
        );
      }
    }
  ];

  const availableTireOptions = useMemo(() => {
    return (tireBatchPickerParams.options || []).filter((o) => {
      const b = o.original;
      if (!b) return false;
      const loc = b.current_location ?? '';
      const stat = b.status || b.join?.tire_item_status || '';
      return loc === '' || stat === 'REMOVE';
    });
  }, [tireBatchPickerParams.options]);

  const availableTireListOptions = useMemo(() => {
    return (tireBatchPickerParams.listOptions || []).filter((o) => {
      const b = o.original;
      if (!b) return false;
      const loc = b.current_location ?? '';
      const stat = b.status || b.join?.tire_item_status || '';
      return loc === '' || stat === 'REMOVE';
    });
  }, [tireBatchPickerParams.listOptions]);

  if (limitInfo && !limitInfo.allowedTx.includes('FIT')) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" fontWeight="bold">
          {`${t('tireCycle.blockedByBackdateRules', 'This transaction is blocked by backdate rules.')} (FIT)`}
        </Typography>
      </Box>
    );
  }

  const handleAddToQueue = () => {
    if (!selectedTireId || !selectedTireData)
      return notify({ message: t('tireCycle.selectTireToInstall', 'Select a tire to install'), severity: 'error' });
    if (!vehicleDetails) return notify({ message: t('tireCycle.selectValidVehicle', 'Select a valid vehicle'), severity: 'error' });
    if (!selectedPosition)
      return notify({ message: t('tireCycle.selectEmptyPositionOnAxle', 'Select an empty position on the axle map'), severity: 'error' });
    if (!kmUnit && kmUnit !== 0) return notify({ message: t('tireCycle.odometerRequired', 'Km Unit is required'), severity: 'error' });

    if (queue.some((q) => q.position === selectedPosition)) {
      return notify({ message: t('tireCycle.positionAlreadyInQueue', 'This position is already in the queue'), severity: 'error' });
    }

    if (queue.some((q) => q.tireId === selectedTireId)) {
      return notify({ message: t('tireCycle.tireAlreadyInQueue', 'This tire is already in the queue'), severity: 'error' });
    }

    const existingAirPressureResult = getValidValueWithSource(
      selectedTireData?.last_inspection?.air_pressure,
      selectedTireData?.air_pressure,
      selectedTireData?.join?.m_tire_spec_pressure_std
    );
    const existingAirPressure = existingAirPressureResult.value;

    const existingTreadDepthResult = getValidValueWithSource(
      selectedTireData?.last_inspection?.tread_depth,
      selectedTireData?.tread_depth,
      selectedTireData?.join?.m_tire_spec_original_tread_depth
    );
    const existingTreadDepth = existingTreadDepthResult.value;

    const hasExistingAirPressure = existingAirPressureResult.source === 'Last Inspection';
    const hasExistingTreadDepth = existingTreadDepthResult.source === 'Last Inspection';

    const originalTreadDepth = parseFloat(
      selectedTireData?.join?.m_tire_spec_original_tread_depth || selectedTireData?.original_tread_depth || 0
    );

    let initialTreadDepth = hasExistingTreadDepth ? existingTreadDepth : bulkTreadDepth !== '' ? bulkTreadDepth : existingTreadDepth;

    if (initialTreadDepth !== '' && initialTreadDepth != null) {
      let val = parseFloat(initialTreadDepth);
      if (!isNaN(val) && originalTreadDepth && val > originalTreadDepth) {
        initialTreadDepth = originalTreadDepth;
      }
    }

    let initialAirPressure = hasExistingAirPressure ? existingAirPressure : bulkAirPressure !== '' ? bulkAirPressure : existingAirPressure;

    const newItem = {
      id: Date.now().toString(),
      position: selectedPosition,
      positionLabel: positionMap[selectedPosition]?.label || selectedPosition,
      tireId: selectedTireId,
      tireData: selectedTireData,
      kmUnit,
      hmUnit,
      airPressure: initialAirPressure != null ? initialAirPressure : '',
      airPressureSource: existingAirPressureResult.source,
      treadDepth: initialTreadDepth != null ? initialTreadDepth : '',
      treadDepthSource: existingTreadDepthResult.source,
      hasExistingAirPressure,
      hasExistingTreadDepth,
      originalTreadDepth
    };

    setQueue([...queue, newItem]);

    // Reset form
    setSelectedTireId(null);
    setSelectedTireData(null);

    notify({
      message: `${newItem.positionLabel}: ${t('tireCycle.addedPositionToQueue', 'Added to queue')}`,
      severity: 'info'
    });
  };

  const handleRemoveFromQueue = (id) => {
    setQueue(queue.filter((q) => q.id !== id));
  };

  const handleSubmitQueue = () => {
    if (queue.length === 0) return;
    if (!mechanicName) return notify({ message: t('tireCycle.mechanicNameRequired', 'Mechanic name is required'), severity: 'error' });
    if (!reason) return notify({ message: t('tireCycle.reasonRequired', 'Reason is required'), severity: 'error' });

    if (isAbnormalKmHm) {
      setConfirmOpen(true);
    } else {
      executeSubmitQueue();
    }
  };

  const executeSubmitQueue = async () => {
    setIsSubmitting(true);

    let currentAxleState = JSON.parse(JSON.stringify(rawAxleData));
    let successfulIds = [];

    for (const item of queue) {
      // update currentAxleState for this item
      if (currentAxleState && currentAxleState.info) {
        let positionFound = false;
        currentAxleState.info.forEach((axle) => {
          (axle.positions || []).forEach((pos) => {
            if (pos.code === item.position) {
              pos.tire_item_id = item.tireData.tire_item || item.tireData.tire_item_id || item.tireData.id;
              pos.serial_number = item.tireData.serial_number || item.tireData.batch_number;
              positionFound = true;
            }
          });
        });

        if (!positionFound) {
          notify({
            message: `${item.positionLabel}: ${t('tireCycle.couldNotFindPosition', 'Could not find position in axle data')}`,
            severity: 'error'
          });
          continue; // Skip this item
        }
      }

      const payload = {
        company: vehicleDetails.company_id || vehicleDetails.company,
        vehicle: vehicleDetails.id,
        tire_item: item.tireData.tire_item || item.tireData.tire_item_id || item.tireData.id,
        tire_equip_vehicle: tireEquipVehicleId || 0,
        serial_number: item.tireData.serial_number || item.tireData.batch_number,
        transaction_type: 'FIT',
        install_date: date || dayjs().format('YYYY-MM-DD'),
        current_position: {
          axle_info: currentAxleState,
          unit_info: {
            km_unit: parseFloat(item.kmUnit) || 0,
            hm_unit: parseFloat(item.hmUnit) || 0
          }
        },
        base_condition: item.tireData.base_condition || item.tireData.condition_before || 'GOOD',
        current_condition: currentCondition,
        mechanic_name: mechanicName,
        reason: reason,
        km_unit: parseFloat(item.kmUnit) || 0,
        hm_unit: parseFloat(item.hmUnit) || 0,
        air_pressure: parseFloat(item.airPressure) || 0,
        tread_depth: parseFloat(item.treadDepth) || 0,
        last_position: item.position
      };

      try {
        await createTireTransaction(payload);
        successfulIds.push(item.id);
      } catch (error) {
        notify({
          message: `${t('tireCycle.failedToInstall', 'Failed to install on')} ${item.positionLabel}: ${error.response?.data?.message || 'Unknown error'}`,
          severity: 'error'
        });
      }
    }

    setQueue((q) => q.filter((item) => !successfulIds.includes(item.id)));

    if (successfulIds.length > 0) {
      notify({
        message: `${t('tireCycle.successfullyInstalled', 'Successfully installed')} ${successfulIds.length} ${t('tireCycle.tires', 'tires')}`,
        severity: 'success'
      });
      if (onSuccess) onSuccess();
    }

    setIsSubmitting(false);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">{t('tireCycle.installTire', 'Install Tire')}</Typography>
        <Button variant="outlined" onClick={onCancel}>
          {t('general.cancel', 'Cancel')}
        </Button>
      </Stack>

      {selectedPosition ? (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('tireCycle.selectedPosition', 'Selected Position:')}
          </Typography>
          <Typography variant="body1" fontWeight="bold" color="primary.main">
            {positionMap[selectedPosition]?.label || selectedPosition}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#fffbeb', borderRadius: 2, border: '1px solid #fde68a' }}>
          <Typography variant="body1" color="warning.dark">
            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  'tireCycle.selectEmptyPosition',
                  'Please select an <strong>empty position</strong> on the Axle Map to install a tire.'
                )
              }}
            />
          </Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item size={{ xs: 6 }}>
          <PickerFieldTable
            label={t('tireCycle.tireSerialNumberAvailable', 'Tire / Serial Number (Available/In Stock) *')}
            infoMessage={t('tireCycle.showingCompatibleTires', 'Showing compatible tires filtered by the selected vehicle type.')}
            {...tireBatchPickerParams}
            options={availableTireOptions}
            listOptions={availableTireListOptions}
            value={selectedTireId}
            columns={tireColumns}
            dialogTitle={t('tireCycle.selectTire', 'Select Tire')}
            multiSelect={false}
            getOptionId={(o) => o?.value || o?.id}
            getOptionLabel={(o) => o?.label || o?.serial_number}
            isRowDisabled={(opt) => {
              return queue.some((q) => q.tireId === opt.value);
            }}
            onChange={(val) => {
              setSelectedTireId(val);
              const opt = availableTireOptions.find((o) => o.value === val);
              setSelectedTireData(opt ? opt.original : null);
            }}
          />
        </Grid>

        <Grid item size={{ xs: 6 }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            onClick={handleAddToQueue}
            disabled={!selectedTireId || !selectedPosition || isSubmitting}
          >
            {t('tireCycle.addToQueue', 'Add to Queue')}
          </Button>
        </Grid>
      </Grid>

      {queue.length > 0 && (
        <Box sx={{ mt: 4, mb: 2, p: 2, bgcolor: '#f1f5f9', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            {t('tireCycle.installationQueue', 'Installation Queue')} ({queue.length})
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('tireCycle.bulkAirPressure', 'Bulk Air Pressure')}
                type="number"
                fullWidth
                value={bulkAirPressure}
                onChange={handleBulkAirPressureChange}
                inputProps={{ min: 0 }}
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === 'e') e.preventDefault();
                }}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('tireCycle.bulkTreadDepth', 'Bulk Tread Depth')}
                type="number"
                fullWidth
                value={bulkTreadDepth}
                onChange={handleBulkTreadDepthChange}
                inputProps={{ min: 0 }}
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === 'e') e.preventDefault();
                }}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label={t('tireCycle.currentCondition', 'Current Condition *')}
                fullWidth
                value={currentCondition}
                onChange={(e) => setcurrentCondition(e.target.value)}
              >
                {['Good', 'Damaged'].map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item size={{ xs: 12 }}>
              <TextField
                label={t('general.mechanicName', 'Mechanic Name *')}
                fullWidth
                value={mechanicName}
                onChange={(e) => setMechanicName(e.target.value)}
              />
            </Grid>

            <Grid item size={{ xs: 12 }}>
              <TextField
                label={t('general.reason', 'Reason *')}
                fullWidth
                multiline
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Grid>
          </Grid>

          <Stack spacing={2} sx={{ my: 1 }}>
            {queue.map((item) => (
              <Box
                key={item.id}
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {t('tireCycle.position', 'Position')}: {item.positionLabel}
                  </Typography>
                  <Typography variant="body2">
                    {t('tireCycle.tire', 'Tire')}: {item.tireData.serial_number || item.tireData.batch_number}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('tireCycle.baseCondition', 'Base Condition')}:{' '}
                    {item.tireData.base_condition || item.tireData.condition_before || 'GOOD'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('tireCardHistory.kmUnit', 'KM Unit')}: {item.kmUnit} | {t('tireCardHistory.hmUnit', 'HM Unit')}: {item.hmUnit}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <TextField
                    label={t('tireCycle.airPressureTh', 'Air Pressure')}
                    size="small"
                    type="number"
                    value={item.airPressure || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQueue(queue.map((q) => (q.id === item.id ? { ...q, airPressure: val } : q)));
                    }}
                    disabled={item.hasExistingAirPressure}
                    sx={{ width: 130 }}
                    inputProps={{ min: 0 }}
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === 'e') e.preventDefault();
                    }}
                    helperText={item.airPressureSource ? `${item.airPressureSource}` : ''}
                    FormHelperTextProps={{ sx: { fontSize: '0.65rem', whiteSpace: 'nowrap' } }}
                  />
                  <TextField
                    label={t('tireCycle.treadDepthTh', 'Tread Depth')}
                    size="small"
                    type="number"
                    value={item.treadDepth || ''}
                    onChange={(e) => {
                      const rawVal = e.target.value;
                      let val = parseFloat(rawVal);
                      if (!isNaN(val) && item.originalTreadDepth && val > item.originalTreadDepth) {
                        val = item.originalTreadDepth;
                        notify({
                          message: `${t('tireCycle.treadDepthCannotExceedOriginal', 'Tread depth cannot exceed original')} (${item.originalTreadDepth} mm)`,
                          severity: 'warning'
                        });
                      }
                      setQueue(queue.map((q) => (q.id === item.id ? { ...q, treadDepth: isNaN(val) ? rawVal : val } : q)));
                    }}
                    disabled={item.hasExistingTreadDepth}
                    sx={{ width: 130 }}
                    inputProps={{ min: 0 }}
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === 'e') e.preventDefault();
                    }}
                    helperText={item.treadDepthSource ? `${item.treadDepthSource}` : ''}
                    FormHelperTextProps={{ sx: { fontSize: '0.65rem', whiteSpace: 'nowrap' } }}
                  />
                  <Button color="error" size="small" onClick={() => handleRemoveFromQueue(item.id)} disabled={isSubmitting}>
                    {t('buttonTitle.remove', 'Remove')}
                  </Button>
                </Box>
              </Box>
            ))}
          </Stack>
          <Button variant="contained" color="primary" fullWidth size="large" onClick={handleSubmitQueue} disabled={isSubmitting}>
            {isSubmitting
              ? t('buttonTitle.processing', 'Processing...')
              : t('tireCycle.confirmAllInstallations', 'Confirm All Installations')}
          </Button>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => !isSubmitting && setConfirmOpen(false)}>
        <DialogTitle>{t('tireCycle.confirmInstallation', 'Confirm Installation')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`${t('tireCycle.areYouSureSubmitInstallation', 'Are you sure you want to submit installation for')} ${queue.length} ${t('tireCycle.tiresQuestion', 'tires?')}`}
          </DialogContentText>
          <Typography color="error" variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
            {t(
              'tireCycle.abnormalKmHmWarning',
              'Warning: The inputted KM and HM values appear to be abnormal. Please verify the data is correct before proceeding.'
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={isSubmitting}>
            {t('general.cancel', 'Cancel')}
          </Button>
          <Button onClick={executeSubmitQueue} variant="contained" color="primary" disabled={isSubmitting}>
            {isSubmitting ? t('general.submitting', 'Submitting...') : t('general.confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
